import React, { useEffect, useState, useCallback } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { CreditCard, SquareCardBrand, useAddCard, useCustomerPaymentMethods } from "@ncs/ncs-api"
import {
	ButtonEcomm,
	Checkbox,
	GridContainer,
	GridItem,
	SquareCardForm,
	SquareCardFormState,
	StateSelectorFormField,
	TextInputFormField,
	useSquareCardContext,
	useToast,
} from "@ncs/web-legos"
import { css } from "@emotion/react"

const SquareFormSchema = z.object({
	givenName: z.string().min(1, "Required"),
	familyName: z.string().min(1, "Required"),
	email: z.string().nullable(),
	organization: z.string().nullable(),
	line2: z.string().min(1, "Required"),
	city: z.string().min(1, "Required"),
	state: z.string().min(1, "Required"),
	postalCode: z.string().min(1, "Required"),
})
type SquareForm = z.infer<typeof SquareFormSchema>
interface SquareFormEcommProps {
	handleSelectSquareCard: () => void
	handleSelectSavedCard: (card: CreditCard) => void
}

export const SquareFormEcomm: React.FC<SquareFormEcommProps> = ({
	handleSelectSquareCard,
	handleSelectSavedCard,
}) => {
	const { makeSuccessToast } = useToast()
	const { card, verifyBuyer } = useSquareCardContext()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const addCard = useAddCard()
	const [paymentMethods] = useCustomerPaymentMethods()
	const [showCardForm, setShowCardForm] = useState(false)
	const [formState, setFormState] = useState<SquareCardFormState>("idle")

	const {
		control,
		handleSubmit,
		formState: { isValid, submitCount },
	} = useForm<SquareForm>({
		resolver: zodResolver(SquareFormSchema),
		defaultValues: {
			givenName: "",
			familyName: "",
			email: null,
			organization: null,
			line2: "",
			city: "",
			state: "",
			postalCode: "",
		},
	})

	const onSubmit = async (formData: SquareForm) => {
		try {
			if (!card) throw new Error("Square card instance not found.")
			setIsSubmitting(true)

			const tokenResult = await card.tokenize()

			if (tokenResult.status === "OK" && tokenResult.token) {
				const verificationResult = await verifyBuyer(tokenResult.token, {
					intent: "STORE",
					billingContact: {
						familyName: formData.familyName,
						givenName: formData.givenName,
						email: formData.email ?? undefined,
						countryCode: "US",
						city: formData.city,
						addressLines: ["", formData.line2],
						postalCode: formData.postalCode,
					},
				})
				if (!verificationResult?.token) throw new Error("Verification token failed.")

				const cardDetails = tokenResult.details?.card

				if (!cardDetails) {
					throw new Error("Card details not found in Square response")
				}
				const isDuplicate = paymentMethods?.cards?.some(
					(savedCard) =>
						savedCard.last4 === cardDetails.last4 &&
						savedCard.expMonth === cardDetails.expMonth &&
						savedCard.expYear === cardDetails.expYear
				)

				if (isDuplicate) {
					setIsSubmitting(false)
					return
				} else {
					const { data } = await addCard({
						provider: "square",
						nonce: tokenResult.token,
						verificationToken: verificationResult.token,
						card: {
							brand: cardDetails.brand as unknown as SquareCardBrand,
							last4: cardDetails.last4,
							expMonth: cardDetails.expMonth,
							expYear: cardDetails.expYear,
							digitalWalletType: "NONE",
						},
						contact: {
							givenName: formData.givenName,
							familyName: formData.familyName,
							email: formData.email ?? "",
							address: {
								organization: formData.organization ?? "",
								line1: "",
								line2: formData.line2,
								city: formData.city,
								state: formData.state,
								postalCode: formData.postalCode,
								country: "US",
							},
						},
					})
					makeSuccessToast("Card successfully added")
					setShowCardForm(false)
					if (data?.card) {
						handleSelectSavedCard(data.card as unknown as CreditCard)
					}
				}
			} else {
				throw new Error("Unable to process card")
			}
		} catch (e) {
			console.log(e)
		} finally {
			setIsSubmitting(false)
			setShowCardForm(false)
			setFormState("submitted")
		}
	}

	const handleFocusClassAdded = useCallback(() => {
		handleSelectSquareCard()
	}, [handleSelectSquareCard])

	useEffect(() => {
		if (!card) return
		card.addEventListener("focusClassAdded", handleFocusClassAdded)

		return () => {
			card.removeEventListener("focusClassAdded", handleFocusClassAdded)
		}
	}, [card, handleFocusClassAdded])

	return (
		<>
			<SquareCardForm formState={formState} setFormState={setFormState} />
			<div css={saveCard}>
				<Checkbox
					onChange={() => setShowCardForm(!showCardForm)}
					value={showCardForm}
					label="Save this card for future transactions"
				/>
			</div>
			{showCardForm && (
				<GridContainer rowGap={0} className="squareFormulary">
					<GridItem xs={12} sm={6}>
						<TextInputFormField
							control={control}
							name="givenName"
							label="First name"
						/>
					</GridItem>
					<GridItem xs={12} sm={6}>
						<TextInputFormField
							control={control}
							name="familyName"
							label="Last name"
						/>
					</GridItem>
					<GridItem xs={12} sm={6}>
						<TextInputFormField
							control={control}
							name="email"
							label="Email address (optional)"
							placeholder="Email address"
						/>
					</GridItem>
					<GridItem xs={12} sm={6}>
						<TextInputFormField
							control={control}
							name="organization"
							label="Organization (optional)"
							placeholder="Organization"
							emptyValueFallback=""
						/>
					</GridItem>
					<GridItem xs={12}>
						<TextInputFormField control={control} name="line2" label="Address" />
					</GridItem>
					<GridItem xs={12} sm={4}>
						<TextInputFormField control={control} name="city" label="City" />
					</GridItem>
					<GridItem xs={6} sm={4}>
						<StateSelectorFormField control={control} name="state" label="State" />
					</GridItem>
					<GridItem xs={6} sm={4}>
						<TextInputFormField
							control={control}
							name="postalCode"
							label="Postal code"
						/>
					</GridItem>
					<ButtonEcomm
						css={{ marginBottom: "1rem", marginTop: "1rem" }}
						customVariant="#0b75e1"
						onClick={handleSubmit(onSubmit)}
						buttonText="Save"
						disabled={submitCount > 0 && !isValid}
						isLoading={isSubmitting}
					/>
				</GridContainer>
			)}
		</>
	)
}

const saveCard = css`
	display: flex;
	color: #374151;
	font-size: 1rem;
	font-weight: 500;
	align-items: center;
	margin-bottom: 1rem;
	margin-left: -3.3rem;

	> div {
		width: auto;
		margin-bottom: 0;
	}

	p {
		font-weight: 500;
	}
`
